import Store from '../../Store/Store';

/**
 * 에러 처리 유틸리티
 * - 에러 타입 분류
 * - 개발자 메일 전송
 * - 사용자용 에러 메시지 생성
 */

const ErrorHandler = {
  /**
   * 에러 타입 분류
   * @param {Error|Object} error - 에러 객체
   * @param {Object} config - axios config (선택)
   * @returns {Object} { type, statusCode, message, userMessage }
   */
  classifyError: (error, config = {}) => {
    // HTTP 에러 (axios response)
    if (error.response) {
      const status = error.response.status;
      const statusText = error.response.statusText || '';
      const data = error.response.data || {};
      const message =
        data.message ||
        data.error ||
        statusText ||
        '알 수 없는 오류가 발생했습니다.';

      // 4xx 에러 (클라이언트 에러)
      if (status >= 400 && status < 500) {
        if (status === 401) {
          return {
            type: 'AUTH_ERROR',
            statusCode: 401,
            message: '인증 토큰이 만료되었습니다.',
            userMessage: '세션이 만료되었습니다. 다시 로그인해주세요.',
            originalMessage: message,
          };
        } else if (status === 403) {
          return {
            type: 'FORBIDDEN',
            statusCode: 403,
            message: '접근 권한이 없습니다.',
            userMessage: '접근 권한이 없습니다. 관리자에게 문의하세요.',
            originalMessage: message,
          };
        } else if (status === 404) {
          return {
            type: 'NOT_FOUND',
            statusCode: 404,
            message: '요청한 리소스를 찾을 수 없습니다.',
            userMessage: '요청한 정보를 찾을 수 없습니다.',
            originalMessage: message,
          };
        } else {
          return {
            type: 'CLIENT_ERROR',
            statusCode: status,
            message: `클라이언트 오류 (${status}): ${message}`,
            userMessage:
              message || '요청을 처리할 수 없습니다. 입력 정보를 확인해주세요.',
            originalMessage: message,
          };
        }
      }
      // 5xx 에러 (서버 에러)
      else if (status >= 500) {
        return {
          type: 'SERVER_ERROR',
          statusCode: status,
          message: `서버 오류 (${status}): ${message}`,
          userMessage: '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
          originalMessage: message,
        };
      }
    }

    // 네트워크 에러
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return {
        type: 'NETWORK_ERROR',
        statusCode: 0,
        message: '요청 시간이 초과되었습니다.',
        userMessage:
          '요청 시간이 초과되었습니다. 네트워크 연결을 확인하고 다시 시도해주세요.',
        originalMessage: error.message,
      };
    }

    if (error.code === 'ERR_NETWORK' || !error.response) {
      return {
        type: 'NETWORK_ERROR',
        statusCode: 0,
        message: '네트워크 연결 오류',
        userMessage: '네트워크 연결을 확인하고 다시 시도해주세요.',
        originalMessage: error.message || 'Network Error',
      };
    }

    // 비즈니스 로직 에러 (서버에서 반환한 에러)
    if (error.isBusinessError) {
      return {
        type: 'BUSINESS_ERROR',
        statusCode: 200, // 비즈니스 에러는 보통 200으로 반환
        message: error.message || '비즈니스 로직 오류',
        userMessage:
          error.userMessage || error.message || '처리 중 오류가 발생했습니다.',
        originalMessage: error.message,
      };
    }

    // 기타 에러
    return {
      type: 'UNKNOWN_ERROR',
      statusCode: 0,
      message: error.message || '알 수 없는 오류가 발생했습니다.',
      userMessage: '오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
      originalMessage: error.message || 'Unknown Error',
    };
  },

  /**
   * 개발자에게 에러 메일 전송
   * @param {Object} errorInfo - 에러 정보
   * @param {Object} config - axios config
   */
  sendErrorEmailToDeveloper: async (errorInfo, config = {}) => {
    try {
      const state = Store.getState();
      const { userInfo } = state.BaseInfo || {};
      const user = userInfo?.[0] || {};

      const errorDetails = {
        type: errorInfo.type,
        statusCode: errorInfo.statusCode,
        message: errorInfo.message,
        originalMessage: errorInfo.originalMessage,
        url: config.url || 'N/A',
        method: config.method || 'N/A',
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        userId: user.userid || 'N/A',
        userName: user.userNm || 'N/A',
        stack: errorInfo.stack || 'N/A',
      };

      const emailBody = `
        <h2>에러 발생 알림</h2>
        <p><strong>에러 타입:</strong> ${errorDetails.type}</p>
        <p><strong>상태 코드:</strong> ${errorDetails.statusCode}</p>
        <p><strong>에러 메시지:</strong> ${errorDetails.message}</p>
        <p><strong>원본 메시지:</strong> ${errorDetails.originalMessage}</p>
        <p><strong>요청 URL:</strong> ${errorDetails.url}</p>
        <p><strong>요청 메서드:</strong> ${errorDetails.method}</p>
        <p><strong>발생 시간:</strong> ${errorDetails.timestamp}</p>
        <p><strong>사용자 ID:</strong> ${errorDetails.userId}</p>
        <p><strong>사용자 이름:</strong> ${errorDetails.userName}</p>
        <p><strong>User Agent:</strong> ${errorDetails.userAgent}</p>
        ${errorDetails.stack !== 'N/A' ? `<p><strong>Stack Trace:</strong><pre>${errorDetails.stack}</pre></p>` : ''}
      `;

      // 개발자 이메일 주소 (환경변수 또는 설정에서 가져오기)
      const developerEmail =
        process.env.REACT_APP_DEVELOPER_EMAIL || 'developer@blunex.co.kr';

      // await Commons.Axios(
      //   'post1',
      //   Global.base.useUrl + Global.urls.SendMail.sendMail,
      //   {
      //     from: Commons.EncryptAES('system@blunex.co.kr') || '',
      //     to: Commons.EncryptAES(developerEmail) || '',
      //     subject: Commons.EncryptAES(`[에러 알림] ${errorDetails.type} - ${errorDetails.statusCode}`) || '',
      //     body: {
      //       html: Commons.EncryptAES(emailBody) || '',
      //     },
      //   }
      // );
    } catch (emailError) {
      // 메일 전송 실패는 조용히 처리 (무한 루프 방지)
      console.error('에러 메일 전송 실패:', emailError);
    }
  },

  /**
   * 에러 처리 (분류 + 메일 전송)
   * @param {Error|Object} error - 에러 객체
   * @param {Object} config - axios config
   * @returns {Object} { errorInfo, shouldShowDialog }
   */
  handleError: async (error, config = {}) => {
    const errorInfo = ErrorHandler.classifyError(error, config);

    // 개발자에게 메일 전송 (401은 제외 - 너무 빈번할 수 있음)
    if (errorInfo.type !== 'AUTH_ERROR') {
      // 스택 트레이스 추가
      if (error.stack) {
        errorInfo.stack = error.stack;
      }
      await ErrorHandler.sendErrorEmailToDeveloper(errorInfo, config);
    }

    // 401 에러는 다이얼로그 표시 필요
    const shouldShowDialog = errorInfo.type === 'AUTH_ERROR';

    return { errorInfo, shouldShowDialog };
  },
};

export default ErrorHandler;
